import { useId, type ClipboardEvent } from 'react';
import { AlertCircle } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../components/ui';

export type DobParts = { day: string; month: string; year: string };

export const EMPTY_DOB: DobParts = { day: '', month: '', year: '' };

/** `YYYY-MM-DD` from the three boxes, or null when it is not a real date. */
export function isoFromParts(parts: DobParts): string | null {
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return null;
  if (parts.year.trim().length !== 4 || year < 1900) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** A whole date pasted into any box: `1980-04-23`, `23/04/1980` or `23 4 1980`. */
function partsFromPaste(text: string): DobParts | null {
  const iso = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/.exec(text);
  if (iso) return { year: iso[1]!, month: iso[2]!, day: iso[3]! };
  const local = /^\s*(\d{1,2})[\s./-]+(\d{1,2})[\s./-]+(\d{4})\s*$/.exec(text);
  if (local) return { day: local[1]!, month: local[2]!, year: local[3]! };
  return null;
}

const BOX = cn(
  'min-h-14 rounded-lg border border-input bg-card px-3.5 py-2.5 text-lg font-medium tabular-nums text-foreground shadow-sm',
  'hover:border-foreground/50 focus-visible:border-primary',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
  'aria-[invalid=true]:border-danger',
);

/**
 * A date of birth in three boxes (day, month, year), the way people say it.
 *
 * Each box has its own label and the browser's `bday-*` autofill, numbers
 * only, and accepts a whole date pasted in. A native date picker is not used:
 * scrolling back forty years on a phone picker is the slowest way there is.
 */
export function DateOfBirth({
  value,
  onChange,
  error,
  legend,
  hint,
  announce = false,
}: {
  value: DobParts;
  onChange: (value: DobParts) => void;
  error?: string | null;
  legend: string;
  hint?: string;
  /** Announce the error when it appears: where nothing else on the page does. */
  announce?: boolean;
}) {
  const { t } = useI18n();
  const id = useId().replace(/:/g, '');
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const parts = partsFromPaste(event.clipboardData.getData('text'));
    if (parts) {
      event.preventDefault();
      onChange(parts);
    }
  };

  const box = (part: keyof DobParts, label: string, size: number, autoComplete: string) => (
    <div>
      <label htmlFor={`${id}-${part}`} className="block text-base font-medium text-foreground">
        {label}
      </label>
      <input
        id={`${id}-${part}`}
        inputMode="numeric"
        autoComplete={autoComplete}
        maxLength={size}
        value={value[part]}
        onPaste={onPaste}
        onChange={(event) => onChange({ ...value, [part]: event.target.value.replace(/\D/g, '') })}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={cn(hintId, errorId) || undefined}
        className={cn(BOX, size === 4 ? 'w-28' : 'w-20')}
      />
    </div>
  );

  return (
    <fieldset aria-describedby={cn(hintId, errorId) || undefined}>
      <legend className="text-lg font-semibold text-foreground">{legend}</legend>
      {hint && (
        <p id={hintId} className="mt-1 text-base text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role={announce ? 'alert' : undefined}
          className="mt-2 flex items-start gap-1.5 text-base font-medium text-danger"
        >
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-4">
        {box('day', t('form.dobDay'), 2, 'bday-day')}
        {box('month', t('form.dobMonth'), 2, 'bday-month')}
        {box('year', t('form.dobYear'), 4, 'bday-year')}
      </div>
    </fieldset>
  );
}
